import { PersistedClient, Persister } from '@tanstack/react-query-persist-client';
import { 
  saveToCache, 
  getFromCache, 
  removeFromCache, 
  clearAllCache,
  isLocalStorageAvailable,
  initializeCacheCleanup
} from './localStorageCache';

/**
 * Custom React Query persister using our localStorage utilities
 * Provides intelligent caching with compression and cleanup
 */
export const createLocalStoragePersister = (): Persister => {
  // Initialize cleanup on first use
  let cleanupInitialized = false;
  let cleanupFunction: (() => void) | null = null;

  const initializeCleanup = () => {
    if (!cleanupInitialized && isLocalStorageAvailable()) {
      cleanupFunction = initializeCacheCleanup();
      cleanupInitialized = true;
    }
  };

  return {
    persistClient: async (client: PersistedClient) => {
      if (!isLocalStorageAvailable()) {
        console.warn('localStorage not available, skipping persistence');
        return;
      }

      initializeCleanup();

      try {
        // Save the entire client state
        const success = saveToCache(['__react_query_client__'], client);
        if (success) {
          console.log('React Query client persisted to localStorage');
        }
      } catch (error) {
        console.error('Failed to persist React Query client:', error);
      }
    },

    restoreClient: async (): Promise<PersistedClient | undefined> => {
      if (!isLocalStorageAvailable()) {
        return undefined;
      }

      initializeCleanup();

      try {
        const client = getFromCache<PersistedClient>(['__react_query_client__']);
        if (client) {
          console.log('React Query client restored from localStorage');
          return client;
        }
      } catch (error) {
        console.error('Failed to restore React Query client:', error);
      }

      return undefined;
    },

    removeClient: async () => {
      if (!isLocalStorageAvailable()) {
        return;
      }

      try {
        removeFromCache(['__react_query_client__']);
        console.log('React Query client removed from localStorage');
      } catch (error) {
        console.error('Failed to remove React Query client:', error);
      }
    },
  };
};

/**
 * Utility to clear all persisted React Query data
 */
export const clearPersistedCache = async (): Promise<void> => {
  if (!isLocalStorageAvailable()) {
    return;
  }

  try {
    clearAllCache();
    console.log('All persisted React Query cache cleared');
  } catch (error) {
    console.error('Failed to clear persisted cache:', error);
  }
};

/**
 * Check if there's persisted data available
 */
export const hasPersistedData = (): boolean => {
  if (!isLocalStorageAvailable()) {
    return false;
  }

  const client = getFromCache(['__react_query_client__']);
  return client !== null;
};